import * as d3 from "d3";
import _ from "lodash";
import moment from "moment";
import datetimeManager from "../managers/datetime";
import mathManager from "../managers/math";
import commonRenderer from "../renderers/common";
import { API_KEYS_BID_ASK_QUANTITY, API_KEYS_SPREAD, CHART_SPREAD, CHART_BID_ASK_QUANTITY } from "../managers/constants";
import { CHART_LABEL_TIME, CHART_LABEL_QUANTITY, CHART_LABEL_SPREAD_TICKS } from "../managers/string-literals";

// Note: This file is still under progress and therefore please do not review it yet

const service = (() => {
  const render = (params) => {
    // Dimensions
    let margin = params.dimensions.margin,
      width = params.dimensions.width,
      height = params.dimensions.height;

    let lineCurveAlpha = 0.5; // Curvature of lines
    let yAxisLabel = null;
    let dualYAxisLabel = null;
    let yDomain = null;
    let dualYDomain = null;
    let yAxisSpreadTooltip = null;

    // Positioning for tick labels on axis
    let xAxisLabel = CHART_LABEL_TIME;
    if (params.primaryChart === CHART_SPREAD) {
      yAxisLabel = CHART_LABEL_SPREAD_TICKS;
      dualYAxisLabel = CHART_LABEL_QUANTITY;
    } else {
      yAxisLabel = CHART_LABEL_QUANTITY;
      dualYAxisLabel = CHART_LABEL_SPREAD_TICKS;
    }

    // Unique class for drag movements across X-axis
    let uniqueLineClass = "line" + params.uniqueKey;

    // Positioning for labels on axis
    let lineStrokeWidth = 1;

    // ==================================================
    // CREATE SVG
    // ==================================================

    let svg = commonRenderer.createSvg(params.drawingContainerElementId, width, height, margin);

    // ==================================================
    // CREATE SCALE
    // ==================================================

    // Time scale for X axis
    let x = d3
      .scaleTime()
      .range([1, width])
      .domain(
        d3.extent(params.data, function(d) {
          return datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset);
        })
      );

    let getScaleForSpread = (spread) => {
      if (params.primaryChart === CHART_SPREAD) {
        return y(spread);
      } else {
        return dualY(spread);
      }
    };

    let getScaleForArea = (area) => {
      if (params.primaryChart === CHART_SPREAD) {
        return dualY(area);
      } else {
        return y(area);
      }
    };

    let drawSpreadAxisTooltip = (id) => {
      if (params.primaryChart === CHART_SPREAD) {
        return commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      } else {
        return commonRenderer.drawChartTooltipDualYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      }
    };

    let drawQantityAreaBidAskAxisTooltip = (id) => {
      if (params.primaryChart === CHART_SPREAD) {
        return commonRenderer.drawChartTooltipDualYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      } else {
        return commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, id, margin, width);
      }
    };

    //returns Y and dualY Domain.
    if (params.primaryChart === CHART_SPREAD) {
      yDomain = commonRenderer.getMinMaxValues(params.data, [API_KEYS_SPREAD.l1]);
      dualYDomain = commonRenderer.getMinMaxValues(params.data, [API_KEYS_BID_ASK_QUANTITY.l1Ask, API_KEYS_BID_ASK_QUANTITY.l1Bid]);
      [dualYDomain[0], dualYDomain[1]] = commonRenderer.recalculateDomainForMirroring(dualYDomain);
    } else {
      yDomain = commonRenderer.getMinMaxValues(params.data, [API_KEYS_BID_ASK_QUANTITY.l1Ask, API_KEYS_BID_ASK_QUANTITY.l1Bid]);
      dualYDomain = commonRenderer.getMinMaxValues(params.data, [API_KEYS_SPREAD.l1]);
      [yDomain[0], yDomain[1]] = commonRenderer.recalculateDomainForMirroring(yDomain);
    }

    // Linear scale for Y axis
    let y = d3
      .scaleLinear()
      .range(params.primaryChart === CHART_SPREAD ? [Math.round(height / 2), 1] : [height, 1]) //starts the ticks from the middle of the height
      .domain(yDomain)
      .nice();

    // Linear scale for Y-Dual axis
    let dualY = d3
      .scaleLinear()
      .range(params.primaryChart === CHART_SPREAD ? [height, 1] : [Math.round(height / 2), 1]) //starts the ticks from the middle of the height
      .domain(dualYDomain)
      .nice();

    // ==================================================
    // FIND MIN & MAX VALUES
    // ==================================================
    // For X axis
    var xDomain = d3.extent(params.data, function(d) {
      return datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset);
    });

    // ==================================================
    // CREATE AXES
    // ==================================================

    // Create X axis
    let xAxis = commonRenderer.createXAxisTimeScale(
      x,
      commonRenderer.generateXAxisTickValues(params.data, params.zoneOffset, params.granularityCode, width),
      params.tickFormat
    );

    // Create Y axis
    let yAxis = commonRenderer.createYAxisLinearScale(y);

    // Create Y-Dual axis
    let dualYAxis = commonRenderer.createDualYAxisLinearScale(dualY);

    // ==================================================
    // DRAW AXES
    // ==================================================
    // Draw X axis

    commonRenderer.drawXAxis(svg, height, xAxis);

    // Draw Y axis
    commonRenderer.drawYAxis(svg, yAxis);

    // Draw Y-Dual axis
    commonRenderer.drawDualYAxis(svg, width, dualYAxis);

    // ==================================================
    // DRAW LABEL on AXES
    // ==================================================

    // Label for X axis
    commonRenderer.drawXAxisLabel(svg, width, height, xAxisLabel);

    // Label for Y axis
    commonRenderer.drawYAxisLabel(svg, height, yAxisLabel, margin);

    // Label for Y-Dual axis
    commonRenderer.drawYDualAxisLabel(svg, width, height, dualYAxisLabel, margin);

    // ==================================================
    // CREATE COMMON TOOLTIP
    // ==================================================
    //common tooltip
    let tooltip = commonRenderer.drawChartDataTooltip(params.drawingContainerElementId, params.uniqueKey);

    // ==================================================
    // DRAW TOOLTIP FOR SPREAD TOP OF THE BOOK FOCUS LINE.
    // ==================================================
    //tooltip on y axis for BID to follow focus line
    yAxisSpreadTooltip = drawSpreadAxisTooltip("yAxisSpreadTooltip");

    // ==================================================
    // CREATE TOOLTIP FOR TOP OF THE BOOK ASK FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    let yAxisTopOfTheBookAskTooltip = drawQantityAreaBidAskAxisTooltip("yAxisTopOfTheBookAskTooltip");

    // ==================================================
    // CREATE TOOLTIP FOR TOP OF THE BOOK BID FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    let yAxisTopOfTheBookBidTooltip = drawQantityAreaBidAskAxisTooltip("yAxisTopOfTheBookBidTooltip");

    // ==================================================
    // HIGHLIGH ROLE SHIFT, ADD ALTERNATE BACKGROUND COLORS.
    // ==================================================
    commonRenderer.highlightRoleShift(params.data, params.zoneOffset, API_KEYS_SPREAD, svg, height, x);

    // ==================================================
    //  DRAWING BID/ASK AREA SHADES
    // ==================================================
    let getArea = function(data) {
      return d3
        .area()
        .x(function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset));
        })
        .y0(function() {
          return getScaleForArea(0);
        })
        .y1(data)
        .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));
    };

    //Drawing area shades.
    svg
      .append("path")
      .datum(
        params.data.filter(function(obj) {
          return obj[API_KEYS_BID_ASK_QUANTITY["l1Bid"]] ? true : false;
        })
      )
      .attr("class", "bid-quan-area-level1-with-ctt " + uniqueLineClass)
      .attr("stroke-width", lineStrokeWidth)
      .attr(
        "d",
        getArea(function(d) {
          return getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY["l1Bid"]]);
        })
      );

    svg
      .append("path")
      .datum(
        params.data.filter(function(obj) {
          return obj[API_KEYS_BID_ASK_QUANTITY["l1Ask"]] ? true : false;
        })
      )
      .attr("class", "ask-quan-area-level1-with-ctt " + uniqueLineClass)
      .attr("stroke-width", lineStrokeWidth)
      .attr(
        "d",
        getArea(function(d) {
          return getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY["l1Ask"]]);
        })
      );

    // ==================================================
    // TOP OF THE BOOK SPREAD LINE
    // ==================================================
    let spreadLevel = d3
      .line()
      .x(function(d) {
        return x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset));
      })
      .y(function(d) {
        return getScaleForSpread(d[API_KEYS_SPREAD["l1"]]);
      })
      .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));

    //Drawing spread Line.
    svg
      .append("path")
      .datum(
        params.data.filter(function(obj) {
          return obj[API_KEYS_SPREAD["l1"]] ? true : false;
        })
      )
      .attr("class", "line spread-line-level1-overlay " + uniqueLineClass)
      .attr("stroke-width", lineStrokeWidth)
      .attr("d", spreadLevel);

    // ==================================================
    // FOCUS LINES
    // ==================================================

    var focus = svg.append("g").style("display", "none");

    //Creating X axis focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineX");

    //top of the book spread Focus Line.
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYTopOfTheBook");

    //Creating top of the book ask focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYAskTopOfTheBook");

    //Creating top of the book bid focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYBidTopOfTheBook");

    //Create Top of the book focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleTopOfTheBook", "focus-point-spread-overlay");

    //Create Top of the book Ask focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleAskTopOfTheBook", "focus-point-quan-area-ask-with-spread");

    //Create Top of the book Bid focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleBidTopOfTheBook", "focus-point-quan-area-bid-with-spread");

    var bisectDate = d3.bisector(function(d) {
      return d[API_KEYS_BID_ASK_QUANTITY.timestamp];
    }).left;

    //drawing RECT in SVG to track mouse position on it
    svg
      .append("rect")
      .attr("class", "overlay")
      .attr("width", width)
      .attr("height", height)
      .on("mouseover", function() {
        focus.style("display", null);
      })
      .on("mouseout", function() {
        tooltip.style("display", "none");
        yAxisSpreadTooltip.style("display", "none");
        yAxisTopOfTheBookAskTooltip.style("display", "none");
        yAxisTopOfTheBookBidTooltip.style("display", "none");
        focus.style("display", "none");
      })
      .on("mousemove", function() {
        //getting current mouse position coordinates.
        var mouse = d3.mouse(this);
        var mouseDate = x.invert(mouse[0]);
        var i = bisectDate(params.data, moment(mouseDate).unix()); // returns the index to the current data item
        i = i >= params.data.length ? params.data.length - 1 : i;

        var d0 = params.data[i - 1]; //previous point
        var d1 = params.data[i]; //current point

        // Used to calculate difference between current data points and previous data points
        let previousDataObject = undefined;

        // work out which date value is closest to the mouse
        if (d0 !== undefined) {
          if (
            moment(mouseDate).unix() - d0[API_KEYS_BID_ASK_QUANTITY.timestamp] >
            d1[API_KEYS_BID_ASK_QUANTITY.timestamp] - moment(mouseDate).unix()
          ) {
            var d = d1;
            previousDataObject = d0;
          } else {
            var d = d0;
            previousDataObject = params.data[i - 2];
          }
          //var d = moment(mouseDate).unix() - d0[API_KEYS_BID_ASK_QUANTITY.timestamp] > d1[API_KEYS_BID_ASK_QUANTITY.timestamp] - moment(mouseDate).unix() ? d1 : d0;
        } else {
          var d = d1;
        }

        var hoveredXValue = x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)); //getting x value of current mouse position

        // ==================================================
        // DRAW COMMON TOOLTIP
        // ==================================================

        let tooltipContent =
          "<tr><td colspan=4>" +
          datetimeManager.getWholeDateTime(
            d[API_KEYS_BID_ASK_QUANTITY.timestamp],
            params.zoneOffset,
            commonRenderer.getXAxisDateFormat(params.granularityCode)
          ) +
          " " +
          params.zoneName +
          " (" +
          params.zoneTiming +
          ")" +
          "</td></tr>";

        tooltipContent += "<tr><td>Contract:</td><td colspan=3>" + d[API_KEYS_SPREAD.contract] + "</td></tr>";
        const inversePercentageIndication = true;

        tooltipContent +=
          "<tr><td>Spread:</td><td colspan=3>" +
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_SPREAD.l1], CHART_SPREAD)) +
          " " +
          commonRenderer.getPercentageChange(
            previousDataObject ? previousDataObject[API_KEYS_SPREAD.l1] : undefined,
            d[API_KEYS_SPREAD.l1],
            params.granularityCode,
            inversePercentageIndication
          ) +
          "</td></tr>";

        tooltipContent +=
          "<tr><td>Quantity:</td><td>" +
          "B: " +
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(Math.abs(d[API_KEYS_BID_ASK_QUANTITY.l1Bid], CHART_BID_ASK_QUANTITY))) +
          " " +
          commonRenderer.getPercentageChange(
            previousDataObject ? previousDataObject[API_KEYS_BID_ASK_QUANTITY.l1Bid] : undefined,
            d[API_KEYS_BID_ASK_QUANTITY.l1Bid],
            params.granularityCode
          ) +
          "</td><td>" +
          "A: " +
          mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(d[API_KEYS_BID_ASK_QUANTITY.l1Ask], CHART_BID_ASK_QUANTITY)) +
          " " +
          commonRenderer.getPercentageChange(
            previousDataObject ? previousDataObject[API_KEYS_BID_ASK_QUANTITY.l1Ask] : undefined,
            d[API_KEYS_BID_ASK_QUANTITY.l1Ask],
            params.granularityCode
          ) +
          "</td><td>(Avg.: " +
          mathManager.toUSANumberFormat(
            commonRenderer.truncateDecimalPointsByChartType((Math.abs(d[API_KEYS_BID_ASK_QUANTITY.l1Bid]) + d[API_KEYS_BID_ASK_QUANTITY.l1Ask]) / 2, CHART_BID_ASK_QUANTITY)
            ) +
          ")</td></tr>";

        //mouse[0] is the x co-ordinate of current mouse position.
        commonRenderer.positionChartDataTooltip(width / 2, mouse[0], tooltip, tooltipContent, margin);

        // ==================================================
        // DRAW X AXIS (VERTICAL) FOCUS LINE.
        // ==================================================
        //drawing X line (vertical line)
        commonRenderer.positionXAxisFocusLine(focus, params.uniqueKey, "focusLineX", hoveredXValue, height);

        // =====================================================================================================
        // POSITION TOP OF THE BOOK FOCUS LINE & TOOLTIP ON IT AND POSITION TOP OF THE BOOK FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_SPREAD.l1]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYTopOfTheBook",
            x(xDomain[0]),
            getScaleForSpread(d[API_KEYS_SPREAD.l1]),
            x(xDomain[1]),
            getScaleForSpread(d[API_KEYS_SPREAD.l1])
          );

          commonRenderer.positionChartTooltipAxis(yAxisSpreadTooltip, d[API_KEYS_SPREAD.l1], getScaleForSpread(d[API_KEYS_SPREAD.l1]), CHART_SPREAD);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset)),
            getScaleForSpread(d[API_KEYS_SPREAD.l1])
          );
        } else {
          focus.select("#focusLineYTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisSpreadTooltip.style("display", "none");
          focus.select("#focusCircleTopOfTheBook" + params.uniqueKey).style("display", "none");
        }

        // =====================================================================================================
        // DRAW TOP OF THE BOOK ASK FOCUS LINE & TOOLTIP ON IT AND DRAW TOP OF THE BOOK ASK FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_BID_ASK_QUANTITY.l1Ask]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYAskTopOfTheBook",
            x(xDomain[0]),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Ask]),
            x(xDomain[1]),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Ask])
          );

          commonRenderer.positionChartTooltipAxis(
            yAxisTopOfTheBookAskTooltip,
            d[API_KEYS_BID_ASK_QUANTITY.l1Ask],
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Ask])
            , CHART_BID_ASK_QUANTITY
          );

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleAskTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Ask])
          );
        } else {
          focus.select("#focusLineYAskTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisTopOfTheBookAskTooltip.style("display", "none");
          focus.select("#focusCircleAskTopOfTheBook" + params.uniqueKey).style("display", "none");
        }

        // =====================================================================================================
        // DRAW TOP OF THE BOOK BID FOCUS LINE & TOOLTIP ON IT AND DRAW TOP OF THE BOOK BID FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_BID_ASK_QUANTITY.l1Bid]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYBidTopOfTheBook",
            x(xDomain[0]),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Bid]),
            x(xDomain[1]),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Bid])
          );

          commonRenderer.positionChartTooltipAxis(
            yAxisTopOfTheBookBidTooltip,
            -d[API_KEYS_BID_ASK_QUANTITY.l1Bid],
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Bid]),
            CHART_BID_ASK_QUANTITY
          );

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleBidTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_BID_ASK_QUANTITY.timestamp], params.zoneOffset)),
            getScaleForArea(d[API_KEYS_BID_ASK_QUANTITY.l1Bid])
          );
        } else {
          focus.select("#focusLineYBidTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisTopOfTheBookBidTooltip.style("display", "none");
          focus.select("#focusCircleBidTopOfTheBook" + params.uniqueKey).style("display", "none");
        }
      });
  }; //End of Render function.
  return { render };
})();

export default service;



// WEBPACK FOOTER //
// ./src/services/renderers/spread-line-with-bid-ask-quantity-area-mirrored.js